import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { fetchOrderCounts } from '../api/orderApi';

interface OrderNotificationContextType {
  unreadCount: number;
  addUnread: () => void;
  resetUnread: () => void;
  // Wholesale signups get their own counter/badge (distinct from orders).
  wholesaleUnread: number;
  addWholesaleUnread: () => void;
  resetWholesaleUnread: () => void;
  // PERSISTENT count of orders still in a pending state (from /orders/counts).
  // Unlike unreadCount, this is NOT reset by viewing — it reflects the real
  // backlog of unprocessed orders and only drops as orders get processed.
  pendingCount: number;
  refreshPending: () => void;
}

const OrderNotificationContext = createContext<OrderNotificationContextType>({
  unreadCount: 0,
  addUnread: () => {},
  resetUnread: () => {},
  wholesaleUnread: 0,
  addWholesaleUnread: () => {},
  resetWholesaleUnread: () => {},
  pendingCount: 0,
  refreshPending: () => {},
});

export const OrderNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(() => {
    const saved = localStorage.getItem('unreadCount');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [wholesaleUnread, setWholesaleUnread] = useState(() => {
    const saved = localStorage.getItem('wholesaleUnreadCount');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = useCallback(async () => {
    try {
      const c = await fetchOrderCounts();
      setPendingCount(Number(c?.pending || 0));
    } catch {
      /* not authed yet / transient — keep the last known value */
    }
  }, []);

  // Fetch on mount and poll, so the badge stays current without a page reload.
  useEffect(() => {
    refreshPending();
    const id = setInterval(refreshPending, 60000);
    return () => clearInterval(id);
  }, [refreshPending]);

  const addUnread = () => {
    setUnreadCount(prev => {
      const next = prev + 1;
      localStorage.setItem('unreadCount', next.toString());
      return next;
    });
    // A new order just arrived — the pending backlog changed too.
    refreshPending();
  };

  const resetUnread = () => {
    localStorage.setItem('unreadCount', '0');
    setUnreadCount(0);
  };

  const addWholesaleUnread = () => {
    setWholesaleUnread(prev => {
      const next = prev + 1;
      localStorage.setItem('wholesaleUnreadCount', next.toString());
      return next;
    });
  };

  const resetWholesaleUnread = () => {
    localStorage.setItem('wholesaleUnreadCount', '0');
    setWholesaleUnread(0);
  };

  // ✅ Memoize the context value to prevent stale state in consumers like Sidebar
  const contextValue = useMemo(() => ({
    unreadCount,
    addUnread,
    resetUnread,
    wholesaleUnread,
    addWholesaleUnread,
    resetWholesaleUnread,
    pendingCount,
    refreshPending,
  }), [unreadCount, wholesaleUnread, pendingCount, refreshPending]);

  return (
    <OrderNotificationContext.Provider value={contextValue}>
      {children}
    </OrderNotificationContext.Provider>
  );
};

export const useOrderNotifications = () => {
  const context = useContext(OrderNotificationContext);
  if (!context) {
    throw new Error('useOrderNotifications must be used within an OrderNotificationProvider');
  }
  return context;
};
